/**
 * "Ordering" on a customer's Settings tab: how a line on this customer's orders gets released (V1 completion O, 02
 * ruling 10) and whether a from-stock line may be delivered in parts as stock arrives (P22). Two switches, each saved
 * at once outside the page's Save bar like the Catalogue section beside it. Owned by orders, so it loads and saves on
 * its own.
 *
 * "Review before release" and its help are Ben's words (decision 10): off, a line releases itself as soon as nothing
 * is outstanding; on, it waits for someone to press Release. "Accepts part deliveries" is provisional wording, for
 * Ben to approve. Hidden for a caller who may not see the customer's orders, and while orders or `v1_completion` is
 * off (the read answers 403 or 404).
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ApiError } from "@shared-core/data/api/errors";
import type { CustomerOrderingSettings } from "@shared-core/data/order-to-invoice-types";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Label } from "../../primitives/label";
import { Skeleton } from "../../primitives/skeleton";
import { Switch } from "../../primitives/switch";

type Load =
  | { kind: "loading" }
  | { kind: "hidden" }
  | { kind: "failed"; message: string }
  | { kind: "loaded"; settings: CustomerOrderingSettings };

export function CustomerOrderingSettingsSection({
  customerKey,
}: {
  customerKey: string;
}) {
  const adapter = useDataAdapter();
  const [load, setLoad] = useState<Load>({ kind: "loading" });
  const [saving, setSaving] = useState(false);

  const reload = useCallback(() => {
    setLoad({ kind: "loading" });
    adapter
      .getCustomerOrderingSettings(customerKey)
      .then((settings) => setLoad({ kind: "loaded", settings }))
      .catch((err: unknown) => {
        if (
          err instanceof ApiError &&
          (err.status === 403 || err.status === 404)
        ) {
          setLoad({ kind: "hidden" });
          return;
        }
        setLoad({
          kind: "failed",
          message: mutationErrorMessage(err, "the server did not answer"),
        });
      });
  }, [adapter, customerKey]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (load.kind === "hidden") return null;

  const save = async (
    change: () => Promise<CustomerOrderingSettings>,
    saved: string,
  ) => {
    setSaving(true);
    try {
      const settings = await change();
      setLoad({ kind: "loaded", settings });
      toast.success(saved);
    } catch (err) {
      toast.error(
        `Not saved: ${mutationErrorMessage(err, "the server did not answer")}`,
      );
    } finally {
      setSaving(false);
    }
  };

  const changeReview = (reviewBeforeRelease: boolean) =>
    save(
      () =>
        adapter.setCustomerReviewBeforeRelease(
          customerKey,
          reviewBeforeRelease,
        ),
      reviewBeforeRelease
        ? "Lines on this customer's orders now wait for someone to press Release."
        : "Lines on this customer's orders now release as soon as nothing is outstanding.",
    );

  const changePartDeliveries = (acceptsPartDeliveries: boolean) =>
    save(
      () =>
        adapter.setCustomerPartDeliveries(customerKey, acceptsPartDeliveries),
      acceptsPartDeliveries
        ? "This customer's orders now accept part deliveries."
        : "This customer's orders no longer accept part deliveries.",
    );

  return (
    <section
      aria-labelledby="ordering-settings-heading"
      className="space-y-4 rounded-lg border border-border p-4 text-sm"
    >
      <h2 id="ordering-settings-heading" className="text-base font-medium">
        Ordering
      </h2>
      {load.kind === "loading" && (
        <div role="status" aria-label="Loading ordering settings">
          <Skeleton className="h-4 w-1/2" />
        </div>
      )}
      {load.kind === "failed" && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            Could not load ordering settings: {load.message}{" "}
            <button type="button" className="underline" onClick={reload}>
              Try again
            </button>
          </AlertDescription>
        </Alert>
      )}
      {load.kind === "loaded" && (
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Switch
              id="ordering-review-before-release"
              checked={load.settings.reviewBeforeRelease}
              disabled={saving || !load.settings.canManage}
              aria-describedby="ordering-review-before-release-help"
              onCheckedChange={(checked) => void changeReview(checked)}
            />
            <div className="space-y-0.5">
              <Label
                htmlFor="ordering-review-before-release"
                className="font-medium"
              >
                Review before release
              </Label>
              <p
                id="ordering-review-before-release-help"
                className="text-sm text-muted-foreground"
              >
                When on, lines on this customer&apos;s orders wait for someone
                to press Release. Otherwise they release as soon as nothing is
                outstanding.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Switch
              id="ordering-accepts-part-deliveries"
              checked={load.settings.acceptsPartDeliveries}
              disabled={saving || !load.settings.canManage}
              aria-describedby="ordering-accepts-part-deliveries-help"
              onCheckedChange={(checked) => void changePartDeliveries(checked)}
            />
            <div className="space-y-0.5">
              <Label
                htmlFor="ordering-accepts-part-deliveries"
                className="font-medium"
              >
                Accepts part deliveries
              </Label>
              <p
                id="ordering-accepts-part-deliveries-help"
                className="text-sm text-muted-foreground"
              >
                When on, a line this customer orders from our stock is sent in
                parts as stock arrives. Otherwise it waits until all of it is in
                stock. An order can choose differently.
              </p>
            </div>
          </div>
          {!load.settings.canManage && (
            <p className="text-xs text-muted-foreground">
              Admins, managers and CSRs change this.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
